import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { maskPhone } from "@shared/notificationCopy";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import WhatsappOptInCheckbox from "@/components/notifications/WhatsappOptInCheckbox";
import {
  getClaimOtpOptions,
  initiateClaimOtp,
  verifyClaimOtp,
  type ClaimOtpOption,
  type ClaimStoreResult,
} from "@/api/claimService";

// ClaimOtpStep — instant claim approval by proving control of the store's REGISTERED
// phone number. Sits between store selection and the evidence form in ClaimFlow.
// The server only exposes masked numbers + opaque ids; the code goes out over WhatsApp
// to the on-record number, never to a number the merchant types.
//
// Fallback contract: whenever OTP isn't possible (flag off, no registered mobiles,
// options fetch fails) the component calls onFallbackToEvidence() immediately and
// SILENTLY — the merchant just sees the normal evidence form, never a reason why.
// A manual-review escape hatch stays visible on every substate.

type ClaimOtpStepProps = {
  store: ClaimStoreResult;
  onFallbackToEvidence: () => void;
  onVerified: () => void | Promise<void>;
  onBack: () => void;
};

type OtpSubstate = "loading" | "choose" | "code";

// Brand skin duplicated from ClaimFlow (same --ys-* tokens) — kept local so the two files
// stay independently editable.
const font = "var(--ys-font)";

const cardStyle: React.CSSProperties = {
  background: "var(--ys-paper)",
  border: "1px solid var(--ys-rule)",
  borderRadius: "var(--ys-r-md)",
  boxShadow: "var(--ys-shadow-2)",
  padding: 24,
  direction: "rtl",
  fontFamily: font,
  maxWidth: 560,
  margin: "32px auto 0px",
};

const headingStyle: React.CSSProperties = {
  fontFamily: font,
  fontSize: 20,
  fontWeight: 600,
  lineHeight: 1.25,
  color: "var(--ys-ink)",
  margin: 0,
};

const bodyStyle: React.CSSProperties = {
  fontFamily: font,
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.5,
  color: "var(--ys-ink-2)",
};

const primaryBtnStyle: React.CSSProperties = {
  background: "var(--ys-primary)",
  color: "#fff",
  border: "none",
  borderRadius: "var(--ys-r)",
  padding: "12px 20px",
  minHeight: 44,
  fontFamily: font,
  fontSize: 16,
  fontWeight: 600,
  cursor: "pointer",
};

const ghostBtnStyle: React.CSSProperties = {
  background: "none",
  border: "1px solid var(--ys-rule-2)",
  borderRadius: "var(--ys-r)",
  padding: "10px 16px",
  minHeight: 44,
  fontFamily: font,
  fontSize: 14,
  fontWeight: 600,
  color: "var(--ys-ink-2)",
  cursor: "pointer",
};

const fieldHint: React.CSSProperties = {
  fontFamily: font,
  fontSize: 14,
  color: "var(--ys-ink-3)",
  fontWeight: 400,
};

const errorText: React.CSSProperties = {
  fontFamily: font,
  fontSize: 14,
  color: "var(--ys-danger)",
  marginTop: 8,
};

const optionRowStyle = (selected: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  gap: 12,
  width: "100%",
  textAlign: "right",
  background: selected ? "var(--ys-bg-2)" : "var(--ys-paper)",
  border: selected ? "1px solid var(--ys-primary)" : "1px solid var(--ys-rule)",
  borderRadius: "var(--ys-r)",
  padding: 12,
  cursor: "pointer",
  fontFamily: font,
  fontSize: 16,
  fontWeight: 600,
  color: "var(--ys-ink)",
});

export default function ClaimOtpStep({
  store,
  onFallbackToEvidence,
  onVerified,
  onBack,
}: ClaimOtpStepProps) {
  const { trackClaim, trackNotifications } = usePostHogTracking();
  const [substate, setSubstate] = useState<OtpSubstate>("loading");
  const [options, setOptions] = useState<ClaimOtpOption[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [sentMasked, setSentMasked] = useState<string | null>(null);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // D-16: explicit WhatsApp consent for the store's number, unchecked by default.
  const [whatsappOptIn, setWhatsappOptIn] = useState(false);

  // Fetch masked options on mount; anything short of a usable list falls back silently.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await getClaimOtpOptions(store.id);
      if (cancelled) return;
      if (!res.ok || !res.data.enabled || res.data.options.length === 0) {
        // OTP impossible (flag off / no registered mobiles / fetch failed) — silent fallback.
        trackClaim.otpFallback({ store_id: store.id, reason: "no_numbers" });
        onFallbackToEvidence();
        return;
      }
      setOptions(res.data.options);
      if (res.data.options.length === 1) setSelectedId(res.data.options[0].id);
      setSubstate("choose");
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.id]);

  const handleSend = async () => {
    if (!selectedId || busy) return;
    setBusy(true);
    setError(null);
    const res = await initiateClaimOtp({ storeId: store.id, phoneId: selectedId });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    trackClaim.otpSent({ store_id: store.id });
    setTransactionId(res.data.transactionId);
    setSentMasked(res.data.masked);
    setOtp("");
    setSubstate("code");
  };

  const handleVerify = async () => {
    if (!transactionId || otp.length !== 6 || busy) return;
    setBusy(true);
    setError(null);
    const res = await verifyClaimOtp({
      storeId: store.id,
      transactionId,
      otp,
      whatsappOptIn,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    trackClaim.otpVerified({ store_id: store.id });
    // Only after a successful verify, and never with the number in props.
    if (whatsappOptIn) {
      trackNotifications.whatsappOptinChanged({ enabled: true, source: "claim_otp" });
    }
    toast.success("تم التحقق من ملكيتك، متجرك مفعّل الآن");
    await onVerified();
  };

  if (substate === "loading") {
    return (
      <div style={{ ...cardStyle, textAlign: "center" }}>
        <p style={bodyStyle}>جارِ التحميل…</p>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <h2 style={headingStyle}>تحقق فوري من ملكية المتجر</h2>

      {substate === "choose" && (
        <>
          <p style={{ ...bodyStyle, marginTop: 8 }}>
            سنرسل رمز تحقق عبر واتساب إلى الرقم المسجل لمتجر «{store.name}». إذا كان
            الرقم بحوزتك، تتم الموافقة على طلبك فوراً.
          </p>

          <div style={{ display: "grid", gap: 8, marginTop: 16 }}>
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setSelectedId(option.id)}
                style={optionRowStyle(selectedId === option.id)}
                aria-pressed={selectedId === option.id}
              >
                <span
                  aria-hidden
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    flexShrink: 0,
                    border:
                      selectedId === option.id
                        ? "5px solid var(--ys-primary)"
                        : "2px solid var(--ys-rule-2)",
                    boxSizing: "border-box",
                  }}
                />
                <span dir="ltr" style={{ letterSpacing: 1 }}>
                  {option.masked}
                </span>
              </button>
            ))}
          </div>

          {error && <p style={errorText}>{error}</p>}

          <button
            type="button"
            disabled={!selectedId || busy}
            style={{
              ...primaryBtnStyle,
              marginTop: 16,
              width: "100%",
              opacity: !selectedId || busy ? 0.6 : 1,
            }}
            onClick={handleSend}
          >
            {busy ? "جارِ الإرسال…" : "أرسل رمز التحقق"}
          </button>
        </>
      )}

      {substate === "code" && (
        <>
          <p style={{ ...bodyStyle, marginTop: 8 }}>
            أدخل الرمز المكوّن من 6 أرقام المرسل عبر واتساب إلى{" "}
            <span dir="ltr" style={{ fontWeight: 600 }}>
              {sentMasked}
            </span>
          </p>

          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleVerify();
            }}
            dir="ltr"
            placeholder="••••••"
            aria-label="رمز التحقق"
            style={{
              width: "100%",
              padding: "12px",
              marginTop: 16,
              borderRadius: "var(--ys-r)",
              border: "1px solid var(--ys-rule-2)",
              background: "var(--ys-paper)",
              fontFamily: font,
              fontSize: 24,
              letterSpacing: 8,
              textAlign: "center",
              color: "var(--ys-ink)",
              boxSizing: "border-box",
            }}
          />

          {/* No resend in v1: the server holds one live transaction per number for 10
              minutes, so a resend inside the window is rejected anyway. */}
          <p style={{ ...fieldHint, marginTop: 8 }}>الرمز صالح لمدة ١٠ دقائق.</p>

          <div style={{ marginTop: 12 }}>
            {/* sentMasked is the server's masked form (e.g. 010•••••678); maskPhone keeps
                only the last 3 digits, so the raw number never reaches the client. */}
            <WhatsappOptInCheckbox
              variant="claim"
              checked={whatsappOptIn}
              onCheckedChange={setWhatsappOptIn}
              phoneMasked={sentMasked ? maskPhone(sentMasked) : undefined}
              disabled={busy}
            />
          </div>

          {error && <p style={errorText}>{error}</p>}

          <button
            type="button"
            disabled={otp.length !== 6 || busy}
            style={{
              ...primaryBtnStyle,
              marginTop: 16,
              width: "100%",
              opacity: otp.length !== 6 || busy ? 0.6 : 1,
            }}
            onClick={handleVerify}
          >
            {busy ? "جارِ التحقق…" : "تأكيد الرمز"}
          </button>
        </>
      )}

      <div
        style={{
          display: "flex",
          gap: 8,
          marginTop: 12,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          style={ghostBtnStyle}
          onClick={() => {
            trackClaim.otpFallback({ store_id: store.id, reason: "manual" });
            onFallbackToEvidence();
          }}
        >
          تخطَّ التحقق وأكمل بالمراجعة اليدوية
        </button>
        <button type="button" style={ghostBtnStyle} onClick={onBack}>
          رجوع للبحث
        </button>
      </div>
    </div>
  );
}
